import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, SpaceBetween, Link, ExpandableSection } from '@amzn/awsui-components-console';
import { ROUTE_CONFIG_BY_PATH } from 'src/config/routeConfig';
import './RecentlyVisited.scss';

const RECENTLY_VISITED_KEY = 'snow_recently_visited';
const MAX_RECENT_ITEMS = 10;

export interface RecentItem {
	path: string;
	label: string;
	timestamp: number;
}

const getRouteLabel = (route: string): string | undefined => {
	const config = ROUTE_CONFIG_BY_PATH[route];

	return config?.text;
};

const getWorkflowNameFromPath = (basePath: string, matchedRoute: string): string | null => {
	if (basePath.startsWith(matchedRoute + '/')) {
		const dynamicPart = basePath.slice(matchedRoute.length + 1);

		if (dynamicPart && dynamicPart.length > 0) {
			return dynamicPart.split('/')[0];
		}
	}

	return null;
};

const getNameFromQueryParams = (fullPath: string): string | null => {
	const queryIndex = fullPath.indexOf('?');

	if (queryIndex === -1) return null;
	
	const queryString = fullPath.slice(queryIndex + 1);
	const params = new URLSearchParams(queryString);
	
	return params.get('name') || params.get('workflowName') || params.get('workflowId') || params.get('id') || params.get('cycleId') || params.get('workflowType') || null;
};

const truncateName = (name: string, maxLength = 20): string => {
	return name.length > maxLength ? name.substring(0, maxLength - 3) + '...' : name;
};

export const getRecentlyVisited = (): RecentItem[] => {
	try {
		const stored = localStorage.getItem(RECENTLY_VISITED_KEY);

		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
};

export const addRecentlyVisited = (fullPath: string, workflowName?: string): void => {
	const [pathPart, queryPart] = fullPath.split('?');
	const basePath = pathPart.split('#')[0];
	
	const matchedRoute = Object.keys(ROUTE_CONFIG_BY_PATH).find(route => 
		basePath === route || basePath.startsWith(route + '/')
	);

	if (!matchedRoute) {
		return; 
	}

	let label = getRouteLabel(matchedRoute);

	if (!label) return;

	let displayName = workflowName;
	
	if (!displayName) {
		displayName = getWorkflowNameFromPath(basePath, matchedRoute) || undefined;
	}
	
	if (!displayName && queryPart) {
		displayName = getNameFromQueryParams(fullPath) || undefined;
	}

	if (displayName) {
		label = `${label}: ${truncateName(displayName)}`;
	}

	const storedPath = queryPart ? `#${basePath}?${queryPart}` : `#${basePath}`;

	const newItem: RecentItem = {
		path: storedPath,
		label,
		timestamp: Date.now(),
	};

	const existing = getRecentlyVisited();
	
	const filtered = existing.filter(item => {
		return item.path !== storedPath;
	});

	const updated = [newItem, ...filtered].slice(0, MAX_RECENT_ITEMS);
	
	localStorage.setItem(RECENTLY_VISITED_KEY, JSON.stringify(updated));
};

export const clearRecentlyVisited = (): void => {
	localStorage.removeItem(RECENTLY_VISITED_KEY);
};

export const useRecentlyVisitedTracker = (): void => {
	const location = useLocation();

	useEffect(() => {
		const path = location.pathname;

		if (path && path !== '/') {
			const fullPath = location.search ? `${path}${location.search}` : path;

			addRecentlyVisited(fullPath);
		}
	}, [location.pathname, location.search]);
};

interface RecentlyVisitedListProps {
	onNavigate?: (path: string) => void;
}

const RecentlyVisitedList: React.FC<RecentlyVisitedListProps> = ({ onNavigate }) => {
	const [items, setItems] = useState<RecentItem[]>([]);
	const location = useLocation();

	useEffect(() => {
		setItems(getRecentlyVisited());
	}, [location.pathname, location.search]);

	if (items.length === 0) {
		return null;
	}

	const formatTimeAgo = (timestamp: number): string => {
		const seconds = Math.floor((Date.now() - timestamp) / 1000);
		
		if (seconds < 60) return 'Just now';
		if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
		if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;

		return `${Math.floor(seconds / 86400)}d ago`;
	};

	return (
		<Box padding={{ horizontal: 's', vertical: 'xs' }}>
			<ExpandableSection
				headerText="Recently Visited"
				variant="footer"
				defaultExpanded={false}
			>
				<SpaceBetween size="xxs">
					{items.map((item, index) => (
						<div
							key={`${item.path}-${index}`}
							style={{
								padding: '4px 0',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
							className="recently-visited-item"
						>
							<Link
								href={item.path}
								variant="secondary"
								fontSize="body-s"
								onFollow={(e) => {
									if (onNavigate) {
										e.preventDefault();
										onNavigate(item.path);
									}
								}}
							>
								{item.label}
							</Link>
							<Box variant="small" color="text-body-secondary" fontSize="body-s">
								{formatTimeAgo(item.timestamp)}
							</Box>
						</div>
					))}
				</SpaceBetween>
			</ExpandableSection>
		</Box>
	);
};

export default RecentlyVisitedList;
